import { startCase } from 'lodash'
import { FormattedMessage } from 'react-intl'

import messages from '@/pages/SpaceCustomAppEditPage/CustomAppEditView/AudienceFields/messages'

let targetAggregates: any

/**
 * @typedef AudienceFilter
 * @property {string | undefined} audienceValue
 * @property {string} audienceType
 * @property {string} expressionOperator
 * @property {string} logicalOperator
 */

/**
 * @param {*} title
 * @param {AudienceFilter[]} audienceFilter
 * @param {*} targetingAggregates
 * @param {*} rulePrefixText
 */
export const defineRule = (title: any, audienceFilter: any, targetingAggregates: any, rulePrefixText?: any) => {
  title = rulePrefixText || (
    <FormattedMessage
      id="SpaceCustomAppEditPage.CustomAppEditForm.MicroUI.AudienceTargeting.DefaultName"
      defaultMessage={`This {name} will be turned on for all users`}
      values={{ name: title }}
    />
  )

  targetAggregates = targetingAggregates
  const reducer = (shouldRuleRun: any, filterObj: any) => {
    return filterObj.audienceType && filterObj.expressionOperator && filterObj.audienceValue ? shouldRuleRun : false
  }
  if (audienceFilter && audienceFilter.reduce(reducer, true)) {
    audienceFilter.forEach((filter: any) => {
      filter.audienceValue = typeof filter.audienceValue === 'boolean' ? '' : filter.audienceValue
    })
    if (audienceFilter.length === 1) {
      return defineRule1Filter(title, audienceFilter)
    }
    if (audienceFilter.length === 2 && audienceFilter[1].logicalOperator) {
      return defineRule2Filter(title, audienceFilter)
    }
    if (audienceFilter.length === 3 && audienceFilter[1].logicalOperator && audienceFilter[2].logicalOperator) {
      if (audienceFilter[1].logicalOperator === audienceFilter[2].logicalOperator) {
        return defineRule3FilterSameLogic(title, audienceFilter, audienceFilter[1].logicalOperator)
      } else {
        return defineRule3FilterDiffLogic(title, audienceFilter)
      }
    }
  }
}

const defineRule1Filter = (title: any, audienceFilter: any) => {
  const { audienceType, expressionOperator, audienceValue, audienceDisplayValue } = audienceFilter[0]
  const expOperator = expressionOperator === 'eq'
  if (audienceType && expressionOperator && audienceValue) {
    return (
      // sentence to describe any rule with only one filter
      <FormattedMessage
        {...messages.defineRule1Filter}
        values={{
          name: title,
          audienceType: showTargetAttribute(audienceType),
          expressionOperator: expOperator ? '' : 'EXCEPT those',
          audienceValue: audienceDisplayValue || audienceValue
        }}
      />
    )
  }
}

const defineRule2Filter = (title: any, audienceFilter: any) => {
  let expressionOperator = ''
  if (audienceFilter[0].expressionOperator === audienceFilter[1].expressionOperator) {
    if (audienceFilter[0].expressionOperator === 'ne') {
      expressionOperator = `EXCEPT those ${audienceFilter[1].logicalOperator === 'and' ? 'both' : 'either'}`
    } else {
      expressionOperator = `${audienceFilter[1].logicalOperator === 'and' ? 'both' : 'either'}`
    }
  } else {
    let first = 0
    let second = 1
    if (audienceFilter[0].expressionOperator === 'ne') {
      first = 1
      second = 0
    }
    return (
      // 2-filter rule with 2 different expression operators
      <FormattedMessage
        {...messages.defineRule2Filter}
        values={{
          name: title,
          expressionOperator1: '',
          audienceType1: showTargetAttribute(audienceFilter[first].audienceType),
          audienceValue1: audienceFilter[first].audienceDisplayValue || audienceFilter[first].audienceValue,
          logicalOperator: audienceFilter[1].logicalOperator === 'or' ? 'OR those' : '',
          expressionOperator2: audienceFilter[1].logicalOperator === 'or' ? 'WITHOUT' : 'EXCEPT those',
          audienceType2: showTargetAttribute(audienceFilter[second].audienceType),
          audienceValue2: audienceFilter[second].audienceDisplayValue || audienceFilter[second].audienceValue
        }}
      />
    )
  }
  return (
    // 2-filter rule with equivalent expression operators
    <FormattedMessage
      {...messages.defineRule2Filter}
      values={{
        name: title,
        expressionOperator1: expressionOperator,
        audienceType1: showTargetAttribute(audienceFilter[0].audienceType),
        audienceValue1: audienceFilter[0].audienceDisplayValue || audienceFilter[0].audienceValue,
        logicalOperator: audienceFilter[1].logicalOperator.toUpperCase(),
        expressionOperator2: '',
        audienceType2: showTargetAttribute(audienceFilter[1].audienceType),
        audienceValue2: audienceFilter[1].audienceDisplayValue || audienceFilter[1].audienceValue
      }}
    />
  )
}

const defineRule3FilterSameLogic = (title: any, audienceFilter: any, logicalOperator: any) => {
  const expressionOperators = audienceFilter.map((filter: any) => filter.expressionOperator === 'eq')
  if (expressionOperators[0] === expressionOperators[1] && expressionOperators[0] === expressionOperators[2]) {
    return (
      // 3-filter rule with the same (2) logical operators and the same (3) expression operators
      <FormattedMessage
        {...messages.defineRule3FilterSameLogicSameExp}
        values={{
          name: title,
          expressionOperator: expressionOperators[0] ? '' : 'EXCEPT those',
          audienceType1: showTargetAttribute(audienceFilter[0].audienceType),
          audienceValue1: audienceFilter[0].audienceDisplayValue || audienceFilter[0].audienceValue,
          logicalOperator: logicalOperator.toUpperCase(),
          audienceType2: showTargetAttribute(audienceFilter[1].audienceType),
          audienceValue2: audienceFilter[1].audienceDisplayValue || audienceFilter[1].audienceValue,
          audienceType3: showTargetAttribute(audienceFilter[2].audienceType),
          audienceValue3: audienceFilter[2].audienceDisplayValue || audienceFilter[2].audienceValue
        }}
      />
    )
  } else {
    let logicGroup
    expressionOperators.filter(Boolean).length === 2 ? (logicGroup = true) : (logicGroup = false)
    const likeFilters = `${showTargetAttribute(audienceFilter[expressionOperators.indexOf(logicGroup)].audienceType)} ${
      audienceFilter[expressionOperators.indexOf(logicGroup)].audienceDisplayValue ||
      audienceFilter[expressionOperators.indexOf(logicGroup)].audienceValue
    } ${logicalOperator.toUpperCase()} ${showTargetAttribute(
      audienceFilter[expressionOperators.lastIndexOf(logicGroup)].audienceType
    )} ${
      audienceFilter[expressionOperators.lastIndexOf(logicGroup)].audienceDisplayValue ||
      audienceFilter[expressionOperators.lastIndexOf(logicGroup)].audienceValue
    }`
    const unlikeFilter = `${showTargetAttribute(
      audienceFilter[expressionOperators.indexOf(!logicGroup)].audienceType
    )} ${
      audienceFilter[expressionOperators.indexOf(!logicGroup)].audienceDisplayValue ||
      audienceFilter[expressionOperators.indexOf(!logicGroup)].audienceValue
    }`

    return (
      // 3-filter rule with the 2 matching expression operators filters grouped together,
      // writing the "equal to" filter or filter group first
      <FormattedMessage
        {...messages.defineRule3FilterSameLogicDiffExp}
        values={{
          name: title,
          audienceA: logicGroup ? likeFilters : unlikeFilter,
          logicalOperator: logicalOperator === 'or' ? logicalOperator.toUpperCase() + ' those' : '',
          audienceB: logicGroup ? unlikeFilter : likeFilters
        }}
      />
    )
  }
}

const defineRule3FilterDiffLogic = (title: any, audienceFilter: any) => {
  const orFilterIndex = audienceFilter[1].logicalOperator === 'or' ? 0 : 2
  const andGroup = audienceFilter[1].logicalOperator === 'or' ? audienceFilter.slice(1) : audienceFilter.slice(0, 2)
  let andLogOp = andGroup[1].logicalOperator
  let bothNot = andGroup[0].expressionOperator === 'eq' ? 'both' : 'NOT'
  if (andGroup[0].expressionOperator !== andGroup[1].expressionOperator) {
    bothNot = ''
    if (andGroup[0].expressionOperator === 'ne') {
      andGroup.unshift(andGroup.pop())
    }
    andLogOp = 'but NOT'
  }
  const andGroupString = `${showTargetAttribute(andGroup[0].audienceType)} ${
    andGroup[0].audienceDisplayValue || andGroup[0].audienceValue
  } ${andLogOp.toUpperCase()} ${showTargetAttribute(andGroup[1].audienceType)} ${
    andGroup[1].audienceDisplayValue || andGroup[1].audienceValue
  }`

  const audiences = [
    `${bothNot} ${andGroupString}`,
    ` ${showTargetAttribute(audienceFilter[orFilterIndex].audienceType)} ${
      audienceFilter[orFilterIndex].audienceDisplayValue || audienceFilter[orFilterIndex].audienceValue
    }`
  ]
  return (
    // 3-filter rule with different logical operators, "and" filters grouped together
    <FormattedMessage
      {...messages.defineRule3FilterDiffLogic}
      values={{
        name: title,
        audienceA: audienceFilter[orFilterIndex].expressionOperator === 'eq' ? audiences[1] : audiences[0],
        audienceB: audienceFilter[orFilterIndex].expressionOperator === 'eq' ? audiences[0] : `NOT ${audiences[1]}`
      }}
    />
  )
}

// based on the selected target audience type, choose the best version to display
const findSelectedTargetAttribute = (selectedAudienceType: any) => {
  return targetAggregates.find((target: any) => {
    return (
      target.scimAttributeName === selectedAudienceType ||
      `${target.scimParentAttributeName}.${target.scimAttributeName}` === selectedAudienceType
    )
  })
}

// returns target audience type differently with string vs boolean
const showTargetAttribute = (audienceType: any) => {
  const currentTarget = findSelectedTargetAttribute(audienceType)
  if (currentTarget) {
    if (currentTarget.type === 'boolean') {
      return (
        'with the ' +
        (currentTarget.scimParentAttributeName
          ? startCase(currentTarget.scimParentAttributeName)
          : currentTarget.displayName) +
        ' status set to'
      )
    }
    return (
      'in the ' +
      (currentTarget.scimParentAttributeName
        ? startCase(currentTarget.scimParentAttributeName)
        : currentTarget.displayName)
    )
  }
  return ''
}
